import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Controller as RHFController } from "react-hook-form";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import type { Company } from "../../../../store/admin/companiesStore";
import type { Role } from "../../../../store/admin/rolesStore";
import type { Status } from "../../../../store/admin/statusesStore";
import type { Device } from "../../../../store/admin/devicesStore";
import type { ThemeClasses } from "../../../../types/components";
import { getCustomSelectStyles } from "../../../../Layouts/admin/shared";
import { getStatusOptionsForEntity } from "../../../../utils/getStatusEntity";
import CompanySelector from "../../../../components/base/CompanySelector";
import { useIsUserDev } from "../../../../hooks/useIsUserDev";
import { NotificationPrefsSection } from "../../../../components/users/NotificationPrefsSection";
import {
  DEFAULT_NOTIFICATION_PREFS,
  type NotificationPrefs,
} from "../../../../features/users/notificationPrefs";

export const DRIVER_ROLE_ID = "vRXfmHHgR5ygbKgjidpA";

export interface DriverEmergencyContact {
  name: string;
  phone: string;
  email: string;
}

export interface UserFormData {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  password?: string;
  phone?: string;
  address?: string;
  bio?: string;
  location?: string;
  website?: string;
  notification_prefs?: NotificationPrefs;
  userType: string;
  company_id: string;
  role_id: string;
  status_id?: string;
  changePassword?: boolean;
  available_devices?: string[];
  // Driver-specific fields
  licenseId?: string;
  licenseExp?: string;
  licenceExpedition?: string;
  contactsEmergency?: DriverEmergencyContact[];
  userDev?: boolean;
  canUseSG?: boolean;
}

interface UserFormProps {
  form: UseFormReturn<UserFormData>;
  isEdit: boolean;
  loadingSubmit: boolean;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  companies: Company[];
  roles: Role[];
  statuses: Status[];
  devices: Device[];
  companiesLoading: boolean;
  rolesLoading: boolean;
  statusesLoading: boolean;
  devicesLoading?: boolean;
  devicesInitialLoading?: boolean;
  showCompanySelector?: boolean;
  onDevicesMenuScrollToBottom?: () => void;
  onSubmit: (data: UserFormData) => void;
  onCancel: () => void;
}

export function UserForm({
  form,
  isEdit,
  loadingSubmit,
  themeClasses,
  isDarkMode,
  companies,
  roles,
  statuses,
  devices,
  companiesLoading,
  rolesLoading,
  statusesLoading,
  devicesLoading,
  devicesInitialLoading,
  showCompanySelector = true,
  onDevicesMenuScrollToBottom,
  onSubmit,
  onCancel,
}: UserFormProps) {
  const { t } = useTranslation("adminSections");
  const { isUserDev } = useIsUserDev();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = form;

  // Watch role_id to show driver fields conditionally
  const watchedRoleId = watch("role_id");
  const isDriverRole = watchedRoleId === DRIVER_ROLE_ID;

  const inputClassName = `w-full text-sm px-4 py-3 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
    } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`;

  // IDs de roles que deben ser excluidos
  const EXCLUDED_ROLE_IDS = [
    "Ne6pwRmFkr4bArJN47NZ", // admin_reseller
    // "vRXfmHHgR5ygbKgjidpA", // driver - Now allowed, shows extra fields
  ];

  // Nombres de roles que deben ser excluidos
  const EXCLUDED_ROLE_NAMES = ["tester"];

  // Filtrar roles para mostrar todos excepto los excluidos
  const availableRoles = roles.filter(
    (role) => !EXCLUDED_ROLE_IDS.includes(role.id) && !EXCLUDED_ROLE_NAMES.includes(role.name.toLowerCase())
  );

  const roleOptions = availableRoles.map((role) => ({
    value: role.name,
    label: t(`users.roles.${role.name}`, role.name),
  }));

  // Opciones de status para usuarios (ACTIVE, INACTIVE)
  const statusOptions = getStatusOptionsForEntity(statuses, "users", t);

  // Opciones de devices
  const deviceOptions = devices.map((device) => {
    const identifier = device.identifier || device.id;
    const label = device.vehicle?.name
      ? `${identifier} (${device.vehicle.name})`
      : identifier;

    return {
      value: device.id,
      label,
    };
  });

  const deviceSelectStyles = {
    ...getCustomSelectStyles<(typeof deviceOptions)[number], true>(isDarkMode),
    menuList: (base: any, state: any) => ({
      ...base,
      padding: "0.5rem",
      maxHeight: state.maxHeight,
    }),
  };

  // Seleccionar/deseleccionar todos los dispositivos cargados
  const allDeviceIds = deviceOptions.map((opt) => opt.value);
  const selectedDeviceIds = form.watch("available_devices") || [];
  const allDevicesSelected =
    allDeviceIds.length > 0 &&
    allDeviceIds.every((id) => selectedDeviceIds.includes(id));
  const handleToggleAllDevices = () => {
    form.setValue("available_devices", allDevicesSelected ? [] : allDeviceIds, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <form
      className="flex flex-col w-full gap-5"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Información básica */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Nombre */}
        <div className="flex flex-col gap-2">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("users.fields.firstName")} <span className="text-red-500">*</span>
          </label>
          <input
            {...register("first_name", {
              required: t("users.form.firstNameRequired"),
              minLength: { value: 2, message: t("users.form.minLength2") },
              maxLength: { value: 50, message: t("users.form.maxLength50") },
            })}
            type="text"
            className={`${inputClassName} ${errors.first_name ? "ring-2 ring-red-500" : ""}`}
            placeholder={t("users.form.firstNamePlaceholder")}
          />
          {errors.first_name && (
            <span className="text-red-500 text-xs">
              {errors.first_name.message}
            </span>
          )}
        </div>

        {/* Apellido */}
        <div className="flex flex-col gap-2">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("users.fields.lastName")} <span className="text-red-500">*</span>
          </label>
          <input
            {...register("last_name", {
              required: t("users.form.lastNameRequired"),
              minLength: { value: 2, message: t("users.form.minLength2") },
              maxLength: { value: 50, message: t("users.form.maxLength50") },
            })}
            type="text"
            className={`${inputClassName} ${errors.last_name ? "ring-2 ring-red-500" : ""}`}
            placeholder={t("users.form.lastNamePlaceholder")}
          />
          {errors.last_name && (
            <span className="text-red-500 text-xs">
              {errors.last_name.message}
            </span>
          )}
        </div>
      </div>

      {/* Username */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("users.fields.username")} <span className="text-red-500">*</span>
        </label>
        <input
          {...register("username", {
            required: t("users.form.usernameRequired"),
            minLength: { value: 3, message: t("users.form.minLength3") },
            maxLength: { value: 30, message: t("users.form.maxLength30") },
            pattern: {
              value: /^[a-zA-Z0-9_]+$/,
              message: t("users.form.onlyAlphanumeric"),
            },
            setValueAs: (value: string) => value?.toLowerCase(),
            onChange: (e) => {
              e.target.value = e.target.value.toLowerCase();
            },
          })}
          type="text"
          className={`${inputClassName} ${errors.username ? "ring-2 ring-red-500" : ""}`}
          placeholder={t("users.form.usernamePlaceholder")}
        />
        <span className={`text-xs ${themeClasses.textSecondary}`}>
          {t("users.form.usernameHint")}
        </span>
        {errors.username && (
          <span className="text-red-500 text-xs">
            {errors.username.message}
          </span>
        )}
      </div>

      {/* Email */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("users.fields.email")} <span className="text-red-500">*</span>
        </label>
        <input
          {...register("email", {
            required: t("users.form.emailRequired"),
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: t("users.form.emailInvalid"),
            },
            setValueAs: (value: string) => value?.toLowerCase(),
            onChange: (e) => {
              e.target.value = e.target.value.toLowerCase();
            },
          })}
          type="email"
          className={`${inputClassName} ${errors.email ? "ring-2 ring-red-500" : ""}`}
          placeholder={t("users.form.emailPlaceholder")}
        />
        {errors.email && (
          <span className="text-red-500 text-xs">{errors.email.message}</span>
        )}
      </div>

      {/* Contraseña - siempre para crear, condicional para editar */}
      {!isEdit ? (
        <div className="flex flex-col gap-2">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("users.fields.password")} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              {...register("password", {
                required: t("users.form.passwordRequired"),
                minLength: { value: 8, message: t("users.form.minLength8") },
              })}
              type={showPassword ? "text" : "password"}
              className={`${inputClassName} pr-10 ${errors.password ? "ring-2 ring-red-500" : ""}`}
              placeholder={t("users.form.passwordPlaceholder")}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={`absolute right-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary} hover:${themeClasses.text}`}
            >
              <i className={`ri-${showPassword ? "eye-off" : "eye"}-line text-lg`}></i>
            </button>
          </div>
          {errors.password && (
            <span className="text-red-500 text-xs">
              {errors.password.message}
            </span>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {/* Checkbox para cambiar contraseña */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showChangePassword}
              onChange={(e) => {
                setShowChangePassword(e.target.checked);
                if (!e.target.checked) {
                  form.setValue("password", "");
                  setConfirmPassword("");
                  setConfirmPasswordError("");
                }
              }}
              className="w-4 h-4 text-primary-500 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 cursor-pointer"
            />
            <span className={`text-sm font-medium ${themeClasses.text}`}>
              {t("users.actions.changePassword")}
            </span>
          </label>

          {/* Campos de contraseña - solo visibles si el checkbox está checado */}
          {showChangePassword && (
            <div className="flex flex-col gap-4 mt-2">
              {/* Nueva contraseña */}
              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("users.fields.newPassword")} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    {...register("password", {
                      required: showChangePassword ? t("users.form.passwordRequired") : false,
                      minLength: { value: 8, message: t("users.form.minLength8") },
                      onChange: (e) => {
                        if (confirmPassword && e.target.value !== confirmPassword) {
                          setConfirmPasswordError(t("users.form.passwordMismatch"));
                        } else {
                          setConfirmPasswordError("");
                        }
                      },
                    })}
                    type={showPassword ? "text" : "password"}
                    className={`${inputClassName} pr-10 ${errors.password ? "ring-2 ring-red-500" : ""}`}
                    placeholder={t("users.form.passwordPlaceholder")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary} hover:${themeClasses.text}`}
                  >
                    <i className={`ri-${showPassword ? "eye-off" : "eye"}-line text-lg`}></i>
                  </button>
                </div>
                {errors.password && (
                  <span className="text-red-500 text-xs">
                    {errors.password.message}
                  </span>
                )}
              </div>

              {/* Confirmar contraseña */}
              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("users.form.confirmPassword")} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      const currentPassword = form.getValues("password");
                      if (e.target.value && currentPassword !== e.target.value) {
                        setConfirmPasswordError(t("users.form.passwordMismatch"));
                      } else {
                        setConfirmPasswordError("");
                      }
                    }}
                    className={`${inputClassName} pr-10 ${confirmPasswordError ? "ring-2 ring-red-500" : ""}`}
                    placeholder={t("users.form.confirmPasswordPlaceholder")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary} hover:${themeClasses.text}`}
                  >
                    <i className={`ri-${showConfirmPassword ? "eye-off" : "eye"}-line text-lg`}></i>
                  </button>
                </div>
                {confirmPasswordError && (
                  <span className="text-red-500 text-xs">
                    {confirmPasswordError}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Teléfono y Dirección */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("users.form.phoneOptional")}
          </label>
          <input
            {...register("phone", {
              validate: (value) => {
                if (!value || value.trim() === "") return true;
                if (value.length < 10) return t("users.form.minLength10");
                if (value.length > 15) return t("users.form.maxLength15");
                return true;
              },
            })}
            type="tel"
            className={`${inputClassName} ${errors.phone ? "ring-2 ring-red-500" : ""}`}
            placeholder={t("users.form.phonePlaceholder")}
          />
          {errors.phone && (
            <span className="text-red-500 text-xs">{errors.phone.message}</span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("users.form.addressOptional")}
          </label>
          <input
            {...register("address")}
            type="text"
            className={inputClassName}
            placeholder={t("users.form.addressPlaceholder")}
          />
        </div>
      </div>

      {/* Perfil público */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("users.form.bio")}
        </label>
        <textarea
          {...register("bio")}
          rows={3}
          className={`${inputClassName} resize-none`}
          placeholder={t("users.form.bioPlaceholder")}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
        <div className="flex flex-col gap-2 min-w-0 w-full">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("users.form.location")}
          </label>
          <input
            {...register("location")}
            type="text"
            className={`${inputClassName} w-full`}
            placeholder={t("users.form.locationPlaceholder")}
          />
        </div>
        <div className="flex flex-col gap-2 min-w-0 w-full">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("users.form.website")}
          </label>
          <input
            {...register("website")}
            type="text"
            className={`${inputClassName} w-full`}
            placeholder={t("users.form.websitePlaceholder")}
          />
        </div>
      </div>

      {/* Compañía - solo visible cuando el reseller necesita seleccionar una */}
      {showCompanySelector ? (
        <div className="flex flex-col gap-2">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("users.fields.company")} <span className="text-red-500">*</span>
          </label>
          <RHFController
            name="company_id"
            control={control}
            rules={{ 
              required: t("users.form.companyRequired"),
            }}
            render={({ field }) => (
              <CompanySelector
                value={field.value || null}
                onChange={(id) => field.onChange(id ?? "")}
                isDarkMode={isDarkMode}
                hasError={!!errors.company_id}
                isDisabled={companiesLoading}
                isLoading={companiesLoading}
                companies={companies}
                placeholder={companiesLoading ? t("users.form.loadingCompanies") : t("users.form.selectCompany")}
              />
            )}
          />
          {errors.company_id && (
            <span className="text-red-500 text-xs">
              {errors.company_id.message}
            </span>
          )}
        </div>
      ) : (
        <input type="hidden" {...register("company_id")} />
      )}

      {/* Rol / Tipo de usuario */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("users.fields.userType")} <span className="text-red-500">*</span>
        </label>
        <RHFController
          name="userType"
          control={control}
          rules={{ required: t("users.form.userTypeRequired") }}
          render={({ field }) => (
            <Select
              {...field}
              options={roleOptions}
              styles={getCustomSelectStyles(isDarkMode, !!errors.userType)}
              placeholder={rolesLoading ? t("users.form.loadingCompanies") : t("users.form.selectType")}
              value={roleOptions.find((opt) => opt.value === field.value)}
              onChange={(option: any) => field.onChange(option?.value)}
              isSearchable
              isLoading={rolesLoading}
              isDisabled={rolesLoading}
              noOptionsMessage={() => t("users.form.noTypesAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
        {errors.userType && (
          <span className="text-red-500 text-xs">
            {errors.userType.message}
          </span>
        )}
      </div>

      {/* Driver-specific fields - only shown when driver role is selected */}
      {isDriverRole && (
        <div className={`flex flex-col gap-4 p-4 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-card/50" : "bg-blue-50/50"}`}>
          <div className="flex items-center gap-2">
            <i className="ri-steering-2-line text-primary-500 text-lg"></i>
            <span className={`text-sm font-semibold ${themeClasses.text}`}>
              {t("users.form.driverInfo", "Información del Conductor")}
            </span>
          </div>

          {/* License ID */}
          <div className="flex flex-col gap-2">
            <label className={`text-sm font-medium ${themeClasses.text}`}>
              {t("users.form.licenseId", "Número de Licencia")} <span className="text-red-500">*</span>
            </label>
            <input
              {...register("licenseId", {
                required: isDriverRole ? t("users.form.licenseIdRequired", "El número de licencia es requerido") : false,
                minLength: { value: 5, message: t("users.form.minLength5", "Mínimo 5 caracteres") },
              })}
              type="text"
              className={`${inputClassName} ${errors.licenseId ? "ring-2 ring-red-500" : ""}`}
              placeholder={t("users.form.licenseIdPlaceholder", "Ej: ABC123456")}
            />
            {errors.licenseId && (
              <span className="text-red-500 text-xs">
                {errors.licenseId.message}
              </span>
            )}
          </div>

          {/* License Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* License Expedition Date */}
            <div className="flex flex-col gap-2">
              <label className={`text-sm font-medium ${themeClasses.text}`}>
                {t("users.form.licenceExpedition", "Fecha de Expedición")}
              </label>
              <input
                {...register("licenceExpedition")}
                type="date"
                className={inputClassName}
              />
            </div>

            {/* License Expiration Date */}
            <div className="flex flex-col gap-2">
              <label className={`text-sm font-medium ${themeClasses.text}`}>
                {t("users.form.licenseExp", "Fecha de Vencimiento")} <span className="text-red-500">*</span>
              </label>
              <input
                {...register("licenseExp", {
                  required: isDriverRole ? t("users.form.licenseExpRequired", "La fecha de vencimiento es requerida") : false,
                })}
                type="date"
                className={`${inputClassName} ${errors.licenseExp ? "ring-2 ring-red-500" : ""}`}
              />
              {errors.licenseExp && (
                <span className="text-red-500 text-xs">
                  {errors.licenseExp.message}
                </span>
              )}
            </div>
          </div>

          {/* Emergency Contact (optional, single contact for simplicity) */}
          <div className="flex flex-col gap-2">
            <label className={`text-sm font-medium ${themeClasses.text}`}>
              {t("users.form.emergencyContact", "Contacto de Emergencia")} <span className={`text-xs ${themeClasses.textSecondary}`}>({t("common.optional", "Opcional")})</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input
                {...register("contactsEmergency.0.name")}
                type="text"
                className={inputClassName}
                placeholder={t("users.form.contactName", "Nombre")}
              />
              <input
                {...register("contactsEmergency.0.phone")}
                type="tel"
                className={inputClassName}
                placeholder={t("users.form.contactPhone", "Teléfono")}
              />
              <input
                {...register("contactsEmergency.0.email")}
                type="email"
                className={inputClassName}
                placeholder={t("users.form.contactEmail", "Email")}
              />
            </div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("users.form.emergencyContactHint", "Persona a contactar en caso de emergencia")}
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("users.fields.userStatus")}
        </label>
        <RHFController
          name="status_id"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={statusOptions}
              styles={getCustomSelectStyles(isDarkMode, !!errors.status_id)}
              placeholder={statusesLoading ? t("users.form.loadingCompanies") : t("users.form.selectStatus")}
              value={statusOptions.find((opt) => opt.value === field.value)}
              onChange={(option: any) => field.onChange(option?.value)}
              isSearchable={false}
              isLoading={statusesLoading}
              isDisabled={statusesLoading}
              noOptionsMessage={() => t("users.form.noStatusesAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
        <span className={`text-xs ${themeClasses.textSecondary}`}>
          {t("users.form.statusHint")}
        </span>
        {errors.status_id && (
          <span className="text-red-500 text-xs">
            {errors.status_id.message}
          </span>
        )}
      </div>

      {/* Dispositivos disponibles - Hidden for driver role */}
      {!isDriverRole && (
        <div className="flex flex-col gap-2">
          {/* «Seleccionar todos» como en GroupForm: la lógica
              (handleToggleAllDevices) ya existía aquí pero nunca se conectó. */}
          <div className="flex items-center justify-between gap-2">
            <label className={`text-sm font-medium ${themeClasses.text}`}>
              {t("users.fields.availableDevices")}
              {selectedDeviceIds.length > 0 && ` (${selectedDeviceIds.length})`}
            </label>
            {allDeviceIds.length > 0 && (
              <button
                type="button"
                onClick={handleToggleAllDevices}
                className="text-xs font-medium px-2.5 py-1 rounded-md bg-primary-500/10 text-primary-500 hover:bg-primary-500/20 border border-primary-500/20 transition-colors"
              >
                {allDevicesSelected
                  ? t("groups.form.deselectAllDevices", "Deseleccionar todos")
                  : `${t("groups.form.selectAllDevices", "Seleccionar todos")} (${allDeviceIds.length})`}
              </button>
            )}
          </div>
          {devicesInitialLoading ? (
            <div className={`flex items-center justify-center py-3 px-4 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
              } border ${themeClasses.cardBorder} rounded-lg`}>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500 mr-2"></div>
              <span className={`text-sm ${themeClasses.textSecondary}`}>
                {t("users.form.loadingDevices")}
              </span>
            </div>
          ) : (
            <RHFController
              name="available_devices"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  options={deviceOptions}
                  styles={deviceSelectStyles}
                  placeholder={t("users.form.selectDevices")}
                  value={deviceOptions.filter((opt) =>
                    field.value?.includes(opt.value)
                  )}
                  onChange={(options) =>
                    field.onChange(options?.map((opt: any) => opt.value) || [])
                  }
                  isSearchable
                  isLoading={devicesLoading}
                  loadingMessage={() => t("users.form.loadingMoreDevices")}
                  onMenuScrollToBottom={onDevicesMenuScrollToBottom}
                  noOptionsMessage={() => t("users.form.noDevicesAvailable")}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  maxMenuHeight={250}
                />
              )}
            />
          )}
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {t("users.form.devicesHint")}
          </span>
        </div>
      )}

      {/* Select oculto para role_id - se llena automáticamente según userType */}
      <input type="hidden" {...register("role_id")} />

      <RHFController
        name="notification_prefs"
        control={control}
        render={({ field }) => (
          <NotificationPrefsSection
            value={field.value ?? DEFAULT_NOTIFICATION_PREFS}
            onChange={field.onChange}
            isDarkMode={isDarkMode}
            translationNamespace="common"
          />
        )}
      />

      {isUserDev && (
        <div
          className={`flex items-start justify-between gap-3 p-4 rounded-lg border ${themeClasses.cardBorder} ${
            isDarkMode ? "bg-dark-card" : "bg-gray-50"
          }`}
        >
          <div className="flex flex-col gap-1">
            <span className={`text-sm font-medium ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-code-box-line text-primary-500"></i>
              {t("users.fields.userDev", "Permisos especiales de desarrollador")}
            </span>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t(
                "users.form.userDevHint",
                "Permite crear, editar y eliminar en integradores, proveedores, fuentes y retransmisiones."
              )}
            </span>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={form.watch("userDev") === true}
              onChange={(e) =>
                form.setValue("userDev", e.target.checked, { shouldDirty: true })
              }
            />
            <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 rounded-full peer peer-checked:bg-primary-500 transition-colors peer-focus:ring-2 peer-focus:ring-primary-500/40 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
          </label>
        </div>
      )}

      {isUserDev && (
        <div
          className={`flex items-start justify-between gap-3 p-4 rounded-lg border ${themeClasses.cardBorder} ${
            isDarkMode ? "bg-dark-card" : "bg-gray-50"
          }`}
        >
          <div className="flex flex-col gap-1">
            <span className={`text-sm font-medium ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-shield-keyhole-line text-primary-500"></i>
              {t("users.fields.canUseSG", "Acceso al módulo SG")}
            </span>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t(
                "users.form.canUseSGHint",
                "Permite entrar a SG y Entrega de servicio. Sin este permiso, el usuario no puede ver esas secciones aunque conozca la URL."
              )}
            </span>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={form.watch("canUseSG") === true}
              onChange={(e) =>
                form.setValue("canUseSG", e.target.checked, { shouldDirty: true })
              }
            />
            <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 rounded-full peer peer-checked:bg-primary-500 transition-colors peer-focus:ring-2 peer-focus:ring-primary-500/40 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
          </label>
        </div>
      )}

      {/* Botones */}
      <div className="flex gap-4 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className={`flex-1 py-3 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
        >
          {t("users.actions.cancel")}
        </button>
        <button
          type="submit"
          disabled={
            loadingSubmit ||
            (isEdit && !isDirty) ||
            (showChangePassword && (confirmPasswordError !== "" || confirmPassword === ""))
          }
          className={`flex-1 py-3 px-4 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors ${
            loadingSubmit ||
            (isEdit && !isDirty) ||
            (showChangePassword && (confirmPasswordError !== "" || confirmPassword === ""))
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          {loadingSubmit
            ? isEdit
              ? t("users.form.submitting")
              : t("users.form.creating")
            : isEdit
              ? t("users.actions.saveChanges")
              : t("users.actions.createUser")}
        </button>
      </div>
    </form>
  );
}
